import { useEffect, useRef, useState } from 'react';
import { View, Text } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Check, X } from 'lucide-react-native';
import { playerStatCardStyles as styles } from './PlayerStatCard.styles';
import { countryCodeFromName } from '../../../src/quiz/helpers/countryFlag.helper';
import { avatarHueStyle, hueFromName } from '../../../src/quiz/helpers/gameDynamicStyles.helper';
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag';
import { HILO_TEST_IDS, HILO_COLORS } from '../../../src/quiz/constants/higherLower.constants';
import type { HiloPlayerCard } from '../../../src/quiz/helpers/higherLowerApi';

interface PlayerStatCardProps {
  card: HiloPlayerCard;
  /** Show the stat value (baseline always; challenger only after the guess is scored). */
  revealed: boolean;
  /** The value to display when revealed (baseline uses card.statValue; challenger uses the reveal). */
  value?: number;
  /** Result badge for the challenger card after a guess. */
  result?: 'correct' | 'wrong' | null;
  variant: 'baseline' | 'challenger';
  /** Phones/small screens: hide the initials avatar to free vertical room (cards were overlapping). */
  compact?: boolean;
}

/** Count up to `target` over ~600ms when `active` flips true (the reveal beat). */
function useCountUp(target: number, active: boolean): number {
  const [display, setDisplay] = useState(active ? target : 0);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    if (!active) { setDisplay(0); return undefined; }
    const duration = 600;
    let start: number | null = null;
    const step = (t: number) => {
      if (start === null) start = t;
      const k = Math.min((t - start) / duration, 1);
      const eased = 1 - (1 - k) ** 3;
      setDisplay(Math.round(target * eased));
      if (k < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, active]);
  return display;
}

export function PlayerStatCard({ card, revealed, value, result, variant, compact = false }: PlayerStatCardProps) {
  const flagCode = countryCodeFromName(card.nationality);
  const hue = hueFromName(card.name);
  const target = value ?? card.statValue ?? 0;
  // Baseline shows its value immediately (no count-up); the challenger counts up on reveal.
  const animate = revealed && variant === 'challenger';
  const counted = useCountUp(target, animate);
  const shown = animate ? counted : target;

  // Show only one club: where the player plays now (active) or their last club (retired) — not the
  // full career list. `card.club` is the player's current/last club name.
  const clubLine = card.club;
  // Complete career totals need no caveat; covered-comps stats still carry "since 2012".
  const statCaption = card.coverageNote ? `${card.statLabel} · ${card.coverageNote}` : card.statLabel;

  const gradientColors: [string, string] =
    variant === 'challenger'
      ? [HILO_COLORS.cardChallengerTop, HILO_COLORS.cardChallengerBottom]
      : [HILO_COLORS.cardGradientTop, HILO_COLORS.cardGradientBottom];

  const cardStyle = [
    styles.card,
    result === 'correct' && styles.cardCorrectGlow,
    result === 'wrong' && styles.cardWrongGlow,
  ];
  const innerStyle = [
    styles.cardInner,
    result === 'correct' && styles.cardCorrect,
    result === 'wrong' && styles.cardWrong,
  ];

  return (
    <View
      style={cardStyle}
      testID={variant === 'baseline' ? HILO_TEST_IDS.baselineCard : HILO_TEST_IDS.challengerCard}
    >
      <LinearGradient
        colors={gradientColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={innerStyle}
      >
        {result ? (
          <View style={[styles.badge, result === 'correct' ? styles.badgeCorrect : styles.badgeWrong]}>
            {result === 'correct' ? <Check size={15} color="#0F1720" /> : <X size={13} color="#0F1720" />}
          </View>
        ) : null}

        {compact ? null : (
          <View style={[styles.avatar, avatarHueStyle(hue)]}>
            <Text style={styles.avatarText}>{card.initials}</Text>
          </View>
        )}

        <View style={styles.nameRow}>
          {flagCode ? (
            <CountryFlag countryCode={flagCode} size="mini" accessibilityLabel={card.nationality ?? ''} />
          ) : null}
          <Text style={styles.name} numberOfLines={1}>{card.name}</Text>
        </View>

        <View style={styles.metaRow}>
          {card.position ? <Text style={styles.metaPill}>{card.position}</Text> : null}
          {card.retired ? (
            <Text style={[styles.metaPill, styles.retiredPill]} testID={HILO_TEST_IDS.retiredPill}>Retired</Text>
          ) : null}
        </View>
        {clubLine ? <Text style={styles.club} numberOfLines={1}>{clubLine}</Text> : null}

        {revealed ? (
          <>
            <Text
              style={[
                styles.statValue,
                result === 'correct' && styles.statValueCorrect,
                result === 'wrong' && styles.statValueWrong,
              ]}
              testID={HILO_TEST_IDS.statValue}
            >
              {shown.toLocaleString('en-US')}
            </Text>
            <Text style={styles.statLabel} testID={HILO_TEST_IDS.coverageLabel}>
              {statCaption}
            </Text>
          </>
        ) : (
          <>
            <Text style={styles.mystery}>?</Text>
            <Text style={styles.mysteryHint}>{`More or fewer ${card.statUnit}?`}</Text>
          </>
        )}
      </LinearGradient>
    </View>
  );
}
